import { useState, useEffect, useCallback } from 'react'

/** Mirrors the AppState interface from main process appStateManager */
interface GlobalState {
  user?: {
    id: string
    email?: string
    user_metadata?: {
      full_name?: string
      avatar_url?: string
      [key: string]: unknown
    }
  } | null
  tasks?: Array<{
    id: string | number
    title: string
    description?: string
    completed: boolean
    cloudId?: string
  }>
  lifeSpaces?: Array<{
    id: string
    name: string
    icon: string
    color?: string
  }>
  activeLifeSpace?: string
  settings?: Record<string, string | number | boolean>
  tabs?: Record<string, Array<{
    id: string
    title: string
    icon?: string
    path: string
    lifeSpace: string
    isDirty?: boolean
    isPinned?: boolean
  }>>
  activeTabId?: Record<string, string>
  onboardingCompleted?: boolean
}

/** Check if Electron IPC API is available */
const hasElectronAPI = () => typeof window !== 'undefined' && !!window.api

/**
 * Hook for accessing global state from Main Process
 *
 * Provides access to the centralized state managed by AppStateManager.
 * Automatically subscribes to state updates via IPC.
 * Falls back to empty state on web (no IPC available).
 *
 * @example
 * ```tsx
 * const { state, updateState } = useGlobalState()
 * const user = state.user
 * const tasks = state.tasks
 * ```
 */
export function useGlobalState() {
  const [state, setState] = useState<GlobalState>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!hasElectronAPI()) {
      setIsLoading(false)
      return
    }

    const loadState = async () => {
      try {
        const initialState = await window.api.invoke('state:get')
        setState(initialState as GlobalState)
      } catch (error) {
        console.error('[useGlobalState] Failed to load initial state:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadState()

    const handleStateUpdate = (data: { key: string; value: unknown }) => {
      setState((prevState) => ({
        ...prevState,
        [data.key]: data.value
      }))
    }

    const handleStateReset = (newState: unknown) => {
      setState(newState as GlobalState)
    }

    window.api.receive('state-updated', handleStateUpdate)
    window.api.receive('state-reset', handleStateReset)

    return () => {
      window.api.removeAllListeners('state-updated')
      window.api.removeAllListeners('state-reset')
    }
  }, [])

  const updateState = useCallback(async (key: string, value: unknown) => {
    if (!hasElectronAPI()) return
    try {
      await window.api.invoke('state:update', key, value)
    } catch (error) {
      console.error('[useGlobalState] Failed to update state:', error)
      throw error
    }
  }, [])

  const getState = useCallback((key: keyof GlobalState) => {
    return state[key]
  }, [state])

  return {
    state,
    updateState,
    getState,
    isLoading
  }
}

/**
 * Hook for accessing user state
 */
export function useGlobalUser() {
  const { state, updateState } = useGlobalState()

  const setUser = useCallback((user: GlobalState['user']) => {
    return updateState('user', user)
  }, [updateState])

  return {
    user: state.user,
    setUser,
    isAuthenticated: !!state.user
  }
}

/**
 * Hook for accessing tasks state
 */
export function useGlobalTasks() {
  const { state, updateState } = useGlobalState()

  const setTasks = useCallback((tasks: NonNullable<GlobalState['tasks']>) => {
    return updateState('tasks', tasks)
  }, [updateState])

  const addTask = useCallback(async (task: NonNullable<GlobalState['tasks']>[0]) => {
    if (!hasElectronAPI()) return
    window.api.send('task:created', task)
  }, [])

  const updateTask = useCallback(async (taskId: string, updates: Partial<NonNullable<GlobalState['tasks']>[0]>) => {
    if (!hasElectronAPI()) return
    window.api.send('task:updated', taskId, updates)
  }, [])

  const deleteTask = useCallback(async (taskId: string) => {
    if (!hasElectronAPI()) return
    window.api.send('task:deleted', taskId)
  }, [])

  return {
    tasks: state.tasks || [],
    setTasks,
    addTask,
    updateTask,
    deleteTask
  }
}

/**
 * Hook for accessing Life Spaces
 */
export function useLifeSpaces() {
  const { state } = useGlobalState()

  const setActiveLifeSpace = useCallback(async (lifeSpaceId: string) => {
    if (!hasElectronAPI()) return
    await window.api.invoke('lifespace:set-active', lifeSpaceId)
  }, [])

  return {
    lifeSpaces: state.lifeSpaces || [],
    activeLifeSpace: state.activeLifeSpace || 'personal',
    setActiveLifeSpace
  }
}

/**
 * Hook for accessing settings
 */
export function useGlobalSettings() {
  const { state } = useGlobalState()

  const updateSettings = useCallback(async (settings: Record<string, string | number | boolean>) => {
    if (!hasElectronAPI()) return
    await window.api.invoke('settings:update', settings)
  }, [])

  return {
    settings: state.settings || {},
    updateSettings
  }
}